import * as React from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { withPageSuspense } from './components/withPageSuspense';
import { usePath, useInitialPath, extractPathAndSearch } from '@components/Routes/usePath';
import { useIsCitiWhitelabel } from '@contexts/app/hooks/useIsCitiWhitelabel';
import { useExperiment } from '@contexts/experiment/useExperiment';
import { universalLoginExperiment } from '@experiments/universalLogin';
import WalletScaPage from '@v2/pages/sca';
const minLoadingTime = 500;

const NotFoundPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/NotFoundPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const OtpLoginPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/OtpLoginPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const SocialSignInPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/SocialSignInPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const ForgotPasswordEmailPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/forgotPasswordEmail/ForgotPasswordEmailPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const ForgotPasswordPhonePage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/forgotPasswordPhone/ForgotPasswordPhonePage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const ChangePasswordPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/changePassword/ChangePasswordPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const SignupEntryPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/SignupEntryPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const WeChatAuthPage = withPageSuspense(React.lazy(() => import('@pages/weChatAuth/WeChatAuthPage')));
const LineAuthPage = withPageSuspense(React.lazy(() => import('@pages/lineAuth/LineAuthPage')));

const SignOutFromAllDevicesPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@v2/pages/forceLogout'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const MfaCitiPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/mfa/MfaPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const VerifyOtpPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/verifyOtp/VerifyOtpPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const PhoneAssociationPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/phoneassociation/PhoneAssociationPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const SigninEntryPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/SigninEntryPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const DeleteAccountEntryPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@pages/DeleteAccountEntryPage'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const WalletResetPinPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@v2/pages/resetPin'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

const WalletAuthPage = withPageSuspense(
    React.lazy(() => {
        return Promise.all([
            import('@v2/pages/walletAuth'),
            new Promise(resolve => setTimeout(resolve, minLoadingTime)),
        ]).then(([moduleExports]) => moduleExports);
    })
);

/**
 * Main app route
 */
const PageRoutes: React.FC = () => {
    const signInPath = usePath('signin');
    const otpLoginPath = usePath('otpLogin');
    const socialSignInPath = usePath('socialSignin');
    const forgotPasswordEmailPath = usePath('forgotpasswordEmail');
    const forgotPasswordPhonePath = usePath('forgotpasswordPhone');
    const signup = usePath('signup');
    const changePasswordPath = usePath('changePassword');
    const weChatAuth = usePath('weChatAuth');
    const lineAuth = usePath('lineAuth');
    const deleteAccount = usePath('deleteAccount');
    const signoutAll = usePath('signoutAll');
    const mfaPath = usePath('mfa');
    const verifyOtpPath = usePath('verifyOtp');
    const phoneAssociationPath = usePath('phoneAssociation');
    const walletResetPinPath = usePath('walletResetPin');
    const walletAuthPath = usePath('walletAuth');
    const walletScaPath = usePath('walletSca');

    const { searchParams, pathname } = extractPathAndSearch(useInitialPath());
    const search = searchParams.toString();

    const allSearchParams = new URLSearchParams(useLocation().search);
    searchParams.forEach((v, k) => !allSearchParams.has(k) && allSearchParams.append(k, v));
    const redirectPathAndSearch = { pathname, search: allSearchParams.toString() };

    return (
        <>
            <Switch>
                {/* UL Path and Culture resolution */}
                {/* routes '/' and '/ul' are for testing */}
                <Redirect exact={true} from="/" to={redirectPathAndSearch} />
                <Redirect exact={true} from="/ul" to={redirectPathAndSearch} />
                <Redirect exact={true} from="/ul/*" to={redirectPathAndSearch} />
                <Redirect exact={true} from="/:code?/ul/login" to={redirectPathAndSearch} />

                {/* UL Pages */}
                <Route exact={true} search={search} path={signInPath} component={SigninEntryPage} />
                <Route exact={true} search={search} path={otpLoginPath} component={OtpLoginPage} />
                <Route exact={true} search={search} path={socialSignInPath} component={SocialSignInPage} />
                <Route exact={true} search={search} path={changePasswordPath} component={ChangePasswordPage} />

                <Route
                    exact={true}
                    search={search}
                    path={forgotPasswordEmailPath}
                    component={ForgotPasswordEmailPage}
                />
                <Route exact={true} search={search} path={verifyOtpPath} component={VerifyOtpPage} />
                <Route exact={true} search={search} path={phoneAssociationPath} component={PhoneAssociationPage} />

                {/* Wallet Pages */}
                <Route exact={true} search={search} path={walletResetPinPath} component={WalletResetPinPage} />
                <Route exact={true} search={search} path={walletAuthPath} component={WalletAuthPage} />
                <Route exact={true} search={search} path={walletScaPath} component={WalletScaPage} />

                <Route
                    exact={true}
                    search={search}
                    path={forgotPasswordPhonePath}
                    component={ForgotPasswordPhonePage}
                />
                <Route exact={true} search={search} path={signup} component={SignupEntryPage} />
                <Route exact={true} path={weChatAuth} component={WeChatAuthPage} />
                <Route exact={true} path={lineAuth} component={LineAuthPage} />
                <Route exact={true} path={deleteAccount} component={DeleteAccountEntryPage} />
                <Route exact={true} path={signoutAll} component={SignOutFromAllDevicesPage} />
                {/* Hardcoded access control for CITI MFA, need to find a better way to do this */}
                <Route exact={true} path={mfaPath} component={useIsCitiWhitelabel() ? MfaCitiPage : NotFoundPage} />
                <Route path="*" search={search} component={NotFoundPage} />
            </Switch>
        </>
    );
};

export default PageRoutes;
